import { useEffect, useRef, useState } from 'react';
import useService from '@modules/ServiceModule/useService';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import { resolveOrgNodeId } from '@modules/AuthModule/lib/resolveOrgNodeId';
import useTasks from '@modules/TasksModule/hooks/useTasks';
import useBookingRetentionEnabled from '@modules/InboxModule/hooks/useBookingRetentionEnabled';
import { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import useFeatureFlag from '@modules/FeatureFlagModule/hooks/useFeatureFlag/useFeatureFlag';
import TaskView from '../TaskView/TaskView';
import MultiTenantTaskView from '../MultiTenantTaskView';
import type { ITask } from '@modules/TasksModule/types';

type Props = {
  taskId: string;
  show?: boolean;
};

/**
 * Whether the tenant-scoped TaskView renders. Retention off: as soon as the
 * task has a tenant. Retention on, MFE: only once the latch has switched, and
 * never back. Retention on, legacy: a stored cross-tenant pick plays the
 * booking's role, so the view stays tenant-less while one is set; the latch is
 * read from the task, so it survives reload and remount, and Back releases it.
 */
function showsTaskView(
  task: ITask,
  isRetentionEnabled: boolean,
  isCustomerMfeEnabled: boolean,
  isSwitched: boolean,
): boolean {
  if (!task.tenantId) {
    return false;
  }
  if (!isRetentionEnabled) {
    return true;
  }
  return isCustomerMfeEnabled ? isSwitched : !task.attributes.customerTenantId;
}

const CustomerTaskView = ({ taskId, show = false }: Props) => {
  const service = useService();
  const { user } = useAuth();
  const { taskById } = useTasks();
  const task = taskById[taskId];
  const isRetentionEnabled = useBookingRetentionEnabled();
  const isCustomerMfeEnabled = useFeatureFlag(
    FeatureFlagKeys.featureEnableAgentInboxForCalls,
    { defaultValue: false },
  );

  // Tenant switch latch (MFE branch). A task that already has a tenant when its
  // view is first built has no booking to lose, so it starts switched.
  const [isSwitched, setIsSwitched] = useState(() => !!task?.tenantId);
  // Reported only by the tenant-less view, so the latch cannot flip back.
  const [isGuarded, setIsGuarded] = useState(false);
  const hadBookingRef = useRef(false);
  // Set by a confirmed Close and never cleared: the card hides before
  // completion re-checks, so no shorter-lived signal would do.
  const [isCloseConfirmed, setIsCloseConfirmed] = useState(false);
  const hasTenant = !!task?.tenantId;

  const handleGuardedChange = (guarded: boolean) => {
    if (guarded) {
      hadBookingRef.current = true;
    }
    setIsGuarded(guarded);
  };

  // A tenant arriving mid-call changes the tree, which rebuilds the MFE. With
  // no booking opened, switch at once (as before); once one was opened, wait
  // until it is closed and the card is hidden, so the CSR never sees a rebuild.
  useEffect(() => {
    if (
      !isRetentionEnabled ||
      !isCustomerMfeEnabled ||
      isSwitched ||
      isCloseConfirmed ||
      !hasTenant ||
      isGuarded
    ) {
      return;
    }
    if (!hadBookingRef.current || !show) {
      setIsSwitched(true);
    }
  }, [
    isRetentionEnabled,
    isCustomerMfeEnabled,
    isSwitched,
    isCloseConfirmed,
    hasTenant,
    isGuarded,
    show,
  ]);

  // Intentionally omits service.error — task rendering is allowed during degraded mode.
  const isServiceLoaded = !service.loading;

  if (!task) {
    return <div>Task not found</div>;
  } else if (!isServiceLoaded) {
    return null;
  } else if (
    showsTaskView(task, isRetentionEnabled, isCustomerMfeEnabled, isSwitched)
  ) {
    return (
      <TaskView
        // The stable view key survives a late-data id re-key; the id would remount.
        key={isRetentionEnabled ? task.viewKey : task.id}
        taskId={task.id}
        show={show}
        taskTenantId={task.tenantId}
        taskOrgNodeId={resolveOrgNodeId(user?.org.tenants ?? [], task.tenantId)}
      />
    );
  } else {
    // in the case for outbound call to a number, we will not have an tenant id,
    // render a multi-tenant task view
    if (isRetentionEnabled) {
      // Stays mounted while hidden so its search, customer and booking survive.
      return (
        <MultiTenantTaskView
          key={task.viewKey}
          taskId={task.id}
          show={show}
          onGuardedChange={handleGuardedChange}
          onCloseConfirmed={() => setIsCloseConfirmed(true)}
        />
      );
    }
    return show && <MultiTenantTaskView key={task.id} taskId={task.id} />;
  }
};

export default CustomerTaskView;
